import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchApiData } from '../Utils/ApiCalls';
import {
  CONVERT_TO_ENTERPRISE,
  GET_CATEGORIES,
  GET_CATEGORIES_ON_FALCON,
  GET_OPTIONAL_FEATURES,
  UPDATE_ENTERPRISE
} from '../urls';
import { Button, Input, Select, showToast } from '@attentive-platform/stem-ui';
import { Checkbox } from '@attentive-platform/stem-ui';

import SearchComponent from './SearchComponent';
import useFormStatus from '../Hooks/useFormStatus';
import {
  BILLING_FREQUENCY,
  BILLING_FREQUENCY_ENUM,
  CHECHBOX_FIELDS,
  CONTRACT_INFO_FIELDS,
  COUNTRY,
  COUNTRY_CODE,
  INITIAL_FORM_DATA,
  FIELDS_TO_REMOVE_FOR_BEAM_USER,
  CHECKBOX_FIELDS_BEAM_USER,
  GROUP_INPUT_DATA_FOR_BEAM_USER,
  CATEGORY_PRICING_TYPES
} from '../Constants/Constants';
import { GROUP_INPUT_DATA } from '../Constants/Constants';
import {
  transformContractData,
  isBeamUser,
  isValidPostalCode,
  setContractInfoData,
  setInitialFormData
} from '../Utils/HelperFunctions';
import DeleteIcon from '../Assets/DeleteIcon';

const EnterpriseConversionForm = ({
  updateEnterprise,
  enterpriseAttributes,
  billingDetails,
  enterpriseContract,
  platformAccess,
  email,
  enterpriseOptionalFeatures,
  totalUsers,
  showCategoryPricing,
  setEmail
}) => {
  const { successMessage, errorMessage, handleApiResult } = useFormStatus();

  const [contractInfo, setContractInfo] = useState({});
  const [formData, setFormData] = useState({});
  const [isManual, setIsManual] = useState(true);
  const [isInvalidZip, setIsInvalidZip] = useState(false);
  const [defaultReport, setDefaultReport] = useState('');
  const [inputValue, setInputValue] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionalFeatures, setOptionalFeatures] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const isBeam = isBeamUser();

  const getOptionalFeatures = async () => {
    const response = await fetchApiData({
      endpoint: GET_OPTIONAL_FEATURES,
      apiRequest: 'GET'
    });
    setOptionalFeatures(response?.data);
  };

  useEffect(() => {
    const fetchOptionalFeatures = async () => {
      if (!isBeam) {
        if (updateEnterprise) {
          const response = await fetchApiData({
            endpoint: GET_OPTIONAL_FEATURES,
            apiRequest: 'GET'
          });

          // Merge existing features with new features
          const existingFeatures = enterpriseOptionalFeatures || [];
          const newFeatures = response?.data || [];

          // Create a map of existing features by identifier
          const existingFeaturesMap = new Map(existingFeatures.map(feature => [feature.identifier, feature]));

          // Add new features that don't exist in the current features
          const mergedFeatures = [...existingFeatures];
          newFeatures.forEach(newFeature => {
            if (!existingFeaturesMap.has(newFeature.identifier)) {
              mergedFeatures.push({
                ...newFeature,
                is_selected: false // Default new features to not selected
              });
            }
          });

          setOptionalFeatures(mergedFeatures);
        } else {
          await getOptionalFeatures();
        }
      }
    };
    fetchOptionalFeatures();
  }, [isBeam, updateEnterprise, enterpriseOptionalFeatures]);

  useEffect(() => {
    const initializeFormData = () => {
      const initialData = setInitialFormData({
        email,
        enterpriseAttributes,
        platformAccess
      });
      setDefaultReport(initialData?.default_report);
      setFormData({
        ...initialData,
        total_users: totalUsers
      });
      const selectedOptions = initialData?.reports_given?.map(report => {
        const label = report?.name;
        const value = report?.id;
        return { label, value };
      });
      setSelectedOptions(selectedOptions);
      setInputValue(selectedOptions.map(option => option?.value));
      if (updateEnterprise) {
        if (!enterpriseAttributes?.is_pilot) {
          // Company is non pilot and company has contract
          // User cannot edit contract details and platform end date
          if (Object.keys(enterpriseContract || {}).length > 0) {
            setIsManual(false);
          }
        }
      }
    };
    const initialiseContractData = () => {
      const initData = setContractInfoData({
        enterpriseContract,
        billingDetails
      });
      setContractInfo(initData);
    };
    setSelectedCategories(Object.keys(enterpriseContract?.trade_wise_pricing || {}).filter(key => key !== 'others'));

    initializeFormData();
    initialiseContractData();
  }, [email, enterpriseAttributes, platformAccess, billingDetails, enterpriseContract, updateEnterprise, totalUsers]);

  const handleSubmit = e => {
    updateEnterpriseContract(e);
  };

  const updateEnterpriseContract = async e => {
    e.preventDefault();
    if (!isValidPostalCode(contractInfo.zip, contractInfo.country) && !isManual) {
      window.scrollTo(0, 0);
      setIsInvalidZip(true);
      return;
    }
    const newFormData = structuredClone(formData);
    if (isBeam) {
      FIELDS_TO_REMOVE_FOR_BEAM_USER.forEach(field => {
        delete newFormData[field];
      });
    }
    const { end_date, total_users, email: formDataEmail, ...ent_attributes } = newFormData;
    const {
      address,
      city,
      zip,
      country,
      addendum_sheet_cost,
      aerial_contract_amount,
      billing_email,
      billing_frequency,
      bpt_contract_amount,
      company_name,
      manual_per_acre_cost,
      manual_threshold_acres,
      manual_worksheet_cost,
      per_acre_cost,
      remeasurement_contract_amount,
      remeasurement_per_acre_cost,
      start_date: contractStartDate,
      end_date: contractEndDate,
      workable_sheet_cost,
      trade_wise_pricing
    } = transformContractData(contractInfo);

    const data = {
      billing_details:
        isBeam || !isManual
          ? {
              name: company_name,
              address,
              city,
              zip,
              country,
              email: billing_email?.toLowerCase()
            }
          : null,
      email: formDataEmail?.toLowerCase(),
      total_users: parseFloat(total_users || 0),
      enterprise_attributes: {
        ...ent_attributes,
        ...(!isBeam && { reports_given: inputValue, default_report: defaultReport })
      },
      platform_access: {
        end_date
      },

      ...(!isBeam && { optional_feature_costs: optionalFeatures }),

      enterprise_contract:
        isBeam || !isManual
          ? {
              addendum_sheet_cost,
              billing_frequency,
              bpt_contract_amount,
              manual_worksheet_cost,
              start_date: contractStartDate + 'T00:00:00+05:30',
              end_date: contractEndDate + 'T00:00:00+05:30',
              workable_sheet_cost,
              ...(showCategoryPricing && { trade_wise_pricing }),
              ...(!isBeam && {
                manual_per_acre_cost,
                manual_threshold_acres,
                aerial_contract_amount,
                per_acre_cost,
                remeasurement_contract_amount,
                remeasurement_per_acre_cost
              })
            }
          : null
    };

    const res = await fetchApiData({
      endpoint: updateEnterprise ? UPDATE_ENTERPRISE : CONVERT_TO_ENTERPRISE,
      data
    });
    handleApiResult(res);
    if (res.error === null) {
      setFormData({ ...INITIAL_FORM_DATA, total_users: 0 });
      setContractInfo(CONTRACT_INFO_FIELDS);
      setIsManual(true);
    } else if (res.error) {
      showToast(res.error, 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-4 border p-4 w-full'>
      <EnterpiseConversion
        successMessage={successMessage}
        errorMessage={errorMessage}
        handleApiResult={handleApiResult}
        isManual={isManual}
        setIsManual={setIsManual}
        formData={formData}
        setFormData={setFormData}
        defaultReport={defaultReport}
        inputValue={inputValue}
        setDefaultReport={setDefaultReport}
        setInputValue={setInputValue}
        setContractInfo={setContractInfo}
        contractInfo={contractInfo}
        updateEnterprise={updateEnterprise}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        isPilot={enterpriseAttributes?.is_pilot}
        isBeam={isBeam}
        optionalFeatures={optionalFeatures}
        setOptionalFeatures={setOptionalFeatures}
        setEmail={setEmail}
      />
      {((!isManual && !formData?.is_pilot) || isBeam) && (
        <EnterpriseBillingInfo
          successMessage={successMessage}
          errorMessage={errorMessage}
          handleApiResult={handleApiResult}
          isInvalidZip={isInvalidZip}
          contractInfo={contractInfo}
          setContractInfo={setContractInfo}
          isBeam={isBeam}
          showCategoryPricing={showCategoryPricing}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
        />
      )}

      <div className='text-center mt-4'>
        <Button color='primary' size='large' variant='contained' type='submit'>
          {updateEnterprise ? 'Update Contract' : 'Convert to Enterprise'}
        </Button>
      </div>
    </form>
  );
};

const EnterpiseConversion = ({
  errorMessage,
  successMessage,
  isManual,
  setIsManual,
  formData,
  setFormData,
  setDefaultReport,
  setInputValue,
  setContractInfo,
  contractInfo,
  updateEnterprise,
  selectedOptions,
  setSelectedOptions,
  isPilot,
  defaultReport,
  isBeam,
  optionalFeatures,
  setOptionalFeatures,
  setEmail
}) => {
  const handleDataChange = (e, field) => {
    if (field === 'is_pilot') {
      setIsManual(true);
    }
    const val = e.target.value;
    setFormData({ ...formData, [field]: val });
  };

  const handleCheckboxChange = (e, field) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  return (
    <div className='border p-4 w-full'>
      {(errorMessage || successMessage) && (
        <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'}`} role='alert'>
          {errorMessage || successMessage}
        </div>
      )}
      <div className='input-group mb-3 '>
        <input
          type='Email'
          value={formData?.email}
          onChange={e => {
            if (setEmail) setEmail(e.target.value);
            handleDataChange(e, 'email');
            setContractInfo({ ...contractInfo, email: e.target.value });
          }}
          className='form-control '
          placeholder='Admin Email'
          aria-label='Email'
          required={!updateEnterprise}
        />
      </div>
      {!isBeam && (
        <SearchComponent
          setInputValue={setInputValue}
          setDefaultReport={setDefaultReport}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          defaultReport={defaultReport}
        />
      )}
      {(isBeam ? GROUP_INPUT_DATA_FOR_BEAM_USER : GROUP_INPUT_DATA).map(({ key, inputs, showLabel, className }) => (
        <div className={className} key={key}>
          {inputs.map(({ valueKey, type, label, ariaLabel, ...props }) => {
            return (
              <React.Fragment key={valueKey}>
                {showLabel && <span className='input-group-text'>{label}</span>}
                <input
                  type={type}
                  value={formData[valueKey]}
                  onChange={e => handleDataChange(e, valueKey)}
                  className='form-control'
                  aria-label={ariaLabel}
                  {...props}
                />
              </React.Fragment>
            );
          })}
        </div>
      ))}

      {!isBeam && optionalFeatures && optionalFeatures.length > 0 && (
        <div className='border rounded p-4 my-4 bg-white max-h-[260px] overflow-y-scroll'>
          <div className='flex justify-between items-center'>
            <h2 className='text-xl font-semibold'>Features added</h2>
          </div>
          <div className='flex items-center justify-between font-semibold border-b'>
            <div className='flex items-center'>
              <Checkbox
                type='checkbox'
                className='w-4'
                checked={optionalFeatures.every(feature => feature.is_selected)}
                onChange={e => {
                  const updated = optionalFeatures.map(feature => ({
                    ...feature,
                    is_selected: e.target.checked
                  }));
                  setOptionalFeatures(updated);
                }}
              />
              <span className='ml-2'>Features</span>
            </div>
            <div className=''>Pricing</div>
          </div>
          {optionalFeatures.map((feature, index) => (
            <div className='flex items-center justify-between mb-2' key={feature.identifier}>
              <div className='flex items-center'>
                <Checkbox
                  className='w-4'
                  type='checkbox'
                  checked={feature.is_selected}
                  onChange={e => {
                    const updated = [...optionalFeatures];
                    updated[index].is_selected = e.target.checked;
                    setOptionalFeatures(updated);
                  }}
                />
                <span className='ml-2'>{feature.name}</span>
              </div>
              <div className='w-1/4 flex items-center'>
                <Input
                  type='number'
                  value={feature.cost}
                  onChange={e => {
                    const updated = [...optionalFeatures];
                    updated[index].cost = e.target.value;
                    setOptionalFeatures(updated);
                  }}
                />
                <span className='ml-2'>$</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {(isBeam ? CHECKBOX_FIELDS_BEAM_USER : CHECHBOX_FIELDS).map(field => (
        <div className='form-check form-check-inline' key={field}>
          <input
            className='form-check-input'
            type='checkbox'
            checked={formData[field]}
            onChange={e => handleCheckboxChange(e, field)}
            id={field}
            disabled={field === 'is_pilot' && !isPilot && updateEnterprise}
          />
          <label className='form-check-label' htmlFor={field}>
            {field.replace(/_/g, ' ').charAt(0).toUpperCase() + field.replace(/_/g, ' ').slice(1)}
          </label>
        </div>
      ))}

      {!isBeam && (
        <div className='form-check form-check-inline border-y-2 w-full mt-4 flex'>
          <label className='form-check-label my-4 '>Billing type:</label>
          <div onClick={() => setIsManual(true)} className='pointer mt-4 mx-2'>
            <input type='radio' checked={isManual} className='mx-2' />
            <label>Manual Billing</label>
          </div>
          {!formData?.is_pilot && (
            <div
              onClick={() => {
                setIsManual(false);
              }}
              className='pointer mt-4'>
              <input type='radio' checked={!isManual} className='mx-2' />
              <label>Automated Billing</label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CategoryRow = React.memo(
  ({ categoryId, categoryName, pricing, isDeletable = true, onChangePricing, onDeleteCategory }) => (
    <div className='px-4 py-3 hover:bg-gray-50 transition-colors'>
      <div className='grid grid-cols-4 gap-4 items-center'>
        <div className='font-medium text-gray-800 truncate' title={categoryName}>
          {categoryName}
        </div>
        <div>
          <input
            required
            type='number'
            min={1}
            step={0.01}
            className='form-control'
            placeholder='Fresh'
            value={pricing?.[CATEGORY_PRICING_TYPES.FRESH] ?? ''}
            onChange={e => onChangePricing(categoryId, CATEGORY_PRICING_TYPES.FRESH, e.target.value)}
          />
        </div>
        <div>
          <input
            required
            type='number'
            min={1}
            step={0.01}
            placeholder='Addendum'
            value={pricing?.[CATEGORY_PRICING_TYPES.ADDENDUM] ?? ''}
            className='form-control'
            onChange={e => onChangePricing(categoryId, CATEGORY_PRICING_TYPES.ADDENDUM, e.target.value)}
          />
        </div>
        <div className='flex justify-center'>
          <button
            type='button'
            onClick={() => onDeleteCategory(categoryId)}
            className={`border-none bg-transparent text-red-500 hover:text-red-700 transition-colors p-1 ${
              isDeletable ? 'cursor-pointer' : '!cursor-not-allowed'
            }`}
            title='Delete category'>
            <DeleteIcon fill={isDeletable ? 'red' : 'gray'} />
          </button>
        </div>
      </div>
    </div>
  )
);

const EnterpriseBillingInfo = ({
  errorMessage,
  successMessage,
  isInvalidZip,
  contractInfo,
  setContractInfo,
  showCategoryPricing,
  isBeam,
  selectedCategories,
  setSelectedCategories
}) => {
  const [categories, setCategories] = useState([]);
  const [fetchingCategories, setFetchingCategories] = useState(false);

  const categoriesOptions = useMemo(() => {
    return categories.map(category => ({
      value: category.id,
      label: category.name
    }));
  }, [categories]);

  const handleDataChange = (val, field) => {
    setContractInfo(prev => ({ ...prev, [field]: val }));
  };

  const handleCategoryPricingChange = useCallback(
    (categoryId, type, value) => {
      setContractInfo(prev => ({
        ...prev,
        trade_wise_pricing: {
          ...prev.trade_wise_pricing,
          [categoryId]: {
            ...prev.trade_wise_pricing[categoryId],
            [type]: value
          }
        }
      }));
    },
    [setContractInfo]
  );

  const handleDeleteCategory = useCallback(
    categoryId => {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));

      setContractInfo(prev => {
        const newPricing = { ...prev.trade_wise_pricing };
        delete newPricing[categoryId];
        return {
          ...prev,
          trade_wise_pricing: newPricing
        };
      });
    },
    [setContractInfo, setSelectedCategories]
  );

  const setDates = date => {
    const year = parseInt(date?.substring(0, 4)) + 1;
    const endDate = year + date?.substring(4);
    setContractInfo({ ...contractInfo, start_date: date, end_date: endDate });
  };

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await fetchApiData({
        endpoint: isBeam ? GET_CATEGORIES : GET_CATEGORIES_ON_FALCON,
        apiRequest: 'GET',
        open: !isBeam
      });
      setCategories(data || []);
    } catch (e) {
      setCategories([]);
      showToast(e.message, 'error');
    } finally {
      setFetchingCategories(false);
    }
  }, [isBeam]);

  useEffect(() => {
    if (showCategoryPricing) {
      fetchCategories();
    }
  }, [showCategoryPricing, fetchCategories]);

  return (
    <div className='border p-4 w-full'>
      {(errorMessage || successMessage || isInvalidZip) && (
        <div className={`alert ${errorMessage || isInvalidZip ? 'alert-danger' : 'alert-success'}`} role='alert'>
          {errorMessage || successMessage || 'Please enter valid zip code'}
        </div>
      )}

      <h1 className='text-2xl '>Enterprise Billing Info</h1>
      <div className='input-group mb-3'>
        <p className='input-group-text w-1/2'>Company Name</p>
        <input
          type='text w-2/3'
          value={contractInfo.company_name}
          onChange={e => handleDataChange(e.target.value, 'company_name')}
          className='form-control '
          placeholder='Company Name'
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Company Billing Email</p>
        <input
          type='email'
          value={contractInfo.billing_email}
          onChange={e => handleDataChange(e.target.value, 'billing_email')}
          className='form-control '
          placeholder='Email'
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Address</p>
        <input
          type='text'
          value={contractInfo.address}
          onChange={e => handleDataChange(e.target.value, 'address')}
          className='form-control '
          placeholder='Address'
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>City</p>
        <input
          type='text'
          value={contractInfo.city}
          onChange={e => handleDataChange(e.target.value, 'city')}
          className='form-control '
          placeholder='City'
          required
        />
      </div>

      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Zipcode</p>
        <input
          type='text'
          value={contractInfo.zip}
          onChange={e => handleDataChange(e.target.value, 'zip')}
          className='form-control '
          placeholder='Zipcode'
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Country</p>
        <div className='dropdown w-1/2'>
          <button
            className='btn border text-dark dropdown-toggle w-full'
            type='button'
            data-toggle='dropdown'
            data-bs-toggle='dropdown'>
            {COUNTRY[contractInfo.country]}
          </button>
          <div className='dropdown-menu w-full'>
            <span className='dropdown-item ' onClick={e => handleDataChange(COUNTRY_CODE.USA, 'country')}>
              United Stated
            </span>
            <span className='dropdown-item' onClick={e => handleDataChange(COUNTRY_CODE.CANADA, 'country')}>
              Canada
            </span>
            <span className='dropdown-item' onClick={e => handleDataChange(COUNTRY_CODE.AUSTRALIA, 'country')}>
              Australia
            </span>
          </div>
        </div>
      </div>
      <h1 className='text-2xl mb-3 mt-5'>Enterprise Contract Info</h1>

      <div className='input-group mb-3'>
        <p className='input-group-text w-1/2'>Contract Start Date</p>
        <input
          type='date'
          value={contractInfo.start_date}
          onChange={e => {
            setDates(e.target.value);
          }}
          className='form-control '
          placeholder=''
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Contract End Date</p>
        <input
          type='date'
          value={contractInfo.end_date}
          onChange={e => {
            setContractInfo({ ...contractInfo, end_date: e.target.value });
          }}
          className='form-control'
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Billing Frequency</p>
        <div className='dropdown w-1/2'>
          <button
            className='btn border text-dark dropdown-toggle w-full'
            type='button'
            data-toggle='dropdown'
            data-bs-toggle='dropdown'>
            {BILLING_FREQUENCY[contractInfo?.billing_frequency]}
          </button>
          <div className='dropdown-menu w-full'>
            <span
              className='dropdown-item '
              onClick={e => handleDataChange(BILLING_FREQUENCY_ENUM.QUATERLY, 'billing_frequency')}>
              {BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.QUATERLY]}
            </span>
            <span
              className='dropdown-item'
              onClick={e => handleDataChange(BILLING_FREQUENCY_ENUM.MONTHLY, 'billing_frequency')}>
              {BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.MONTHLY]}
            </span>
            <span
              className='dropdown-item'
              onClick={e => handleDataChange(BILLING_FREQUENCY_ENUM.HALF_YEARLY, 'billing_frequency')}>
              {BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.HALF_YEARLY]}
            </span>
            <span
              className='dropdown-item'
              onClick={e => handleDataChange(BILLING_FREQUENCY_ENUM.ANNUAL, 'billing_frequency')}>
              {BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.ANNUAL]}
            </span>
          </div>
        </div>
      </div>
      {!isBeam && (
        <>
          <div className='input-group mb-3 '>
            <p className='input-group-text w-1/2'>Aerial Contract Amount ($)</p>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.aerial_contract_amount}
              onChange={e => handleDataChange(e.target.value, 'aerial_contract_amount')}
              className='form-control '
              placeholder=''
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <p className='input-group-text w-1/2 text-wrap'>Aerial Per Acre Cost ($)</p>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.per_acre_cost}
              onChange={e => handleDataChange(e.target.value, 'per_acre_cost')}
              className='form-control'
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <p className='input-group-text w-1/2 text-wrap'>
              Per Acre Imagery Cost for Manual Measurement, Beyond Threshold Acres ($):
            </p>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.manual_per_acre_cost}
              onChange={e => handleDataChange(e.target.value, 'manual_per_acre_cost')}
              className='form-control'
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <p className='input-group-text w-1/2 text-wrap'>
              Threshold Acres for Imagery cost in Manual Measurments ($):
            </p>
            <input
              type='number'
              step={0.01}
              min={1}
              defaultValue='20'
              value={contractInfo.manual_threshold_acres}
              onChange={e => handleDataChange(e.target.value, 'manual_threshold_acres')}
              className='form-control'
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <p className='input-group-text w-1/2 text-wrap'>Aerial Remeasurement Contract Amount ($)</p>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.remeasurement_contract_amount}
              onChange={e => handleDataChange(e.target.value, 'remeasurement_contract_amount')}
              className='form-control '
              required
            />
          </div>
          <div className='input-group mb-3 '>
            <p className='input-group-text w-1/2 text-wrap'>Aerial Remeasurement Per Acre Cost ($)</p>
            <input
              type='number'
              step={0.01}
              min={1}
              value={contractInfo.remeasurement_per_acre_cost}
              onChange={e => handleDataChange(e.target.value, 'remeasurement_per_acre_cost')}
              className='form-control '
              required
            />
          </div>
        </>
      )}
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2 text-wrap'>Contract Amount for BPT ($)</p>
        <input
          type='number'
          step={0.01}
          min={1}
          value={contractInfo.bpt_contract_amount}
          onChange={e => handleDataChange(e.target.value, 'bpt_contract_amount')}
          className='form-control '
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2 text-wrap'>Workable Sheet Cost BPT ($)</p>
        <input
          type='number'
          step={0.01}
          min={1}
          value={contractInfo.workable_sheet_cost}
          onChange={e => handleDataChange(e.target.value, 'workable_sheet_cost')}
          className='form-control '
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2'>Addendum Sheet Cost BPT ($)</p>
        <input
          type='number'
          step={0.01}
          min={1}
          value={contractInfo.addendum_sheet_cost}
          onChange={e => handleDataChange(e.target.value, 'addendum_sheet_cost')}
          className='form-control '
          required
        />
      </div>
      <div className='input-group mb-3 '>
        <p className='input-group-text w-1/2 text-wrap'>Workable Sheet in Manual Measurement for BPT ($)</p>
        <input
          type='number'
          step={0.01}
          min={1}
          value={contractInfo.manual_worksheet_cost}
          onChange={e => handleDataChange(e.target.value, 'manual_worksheet_cost')}
          className='form-control '
          placeholder=''
          required
        />
      </div>

      {showCategoryPricing && (
        <>
          <h1 className='text-2xl mb-3 mt-5'>Categories Info</h1>

          <Select
            allowClear
            allowClearIcon
            size='large'
            multiple
            isLoading={fetchingCategories}
            color='secondary'
            items={categoriesOptions}
            value={selectedCategories}
            onChange={setSelectedCategories}
            placeholder='Select Categories'
            variant='std-filled'
            className='mb-5'
          />

          <div className='mb-5'>
            <h3 className='text-lg font-semibold mb-3'>Category Pricing ($)</h3>
            <div className='bg-white border rounded-lg overflow-hidden'>
              <div className='bg-gray-50 px-4 py-3 border-b'>
                <div className='grid grid-cols-4 gap-4 font-medium text-gray-700'>
                  <div>Category</div>
                  <div>Fresh</div>
                  <div>Addendum</div>
                  <div>Action</div>
                </div>
              </div>
              <div className='divide-y'>
                {selectedCategories.map(categoryId => {
                  const category = categories.find(cat => cat.id === categoryId);
                  const pricing = contractInfo?.trade_wise_pricing?.[categoryId] || {};
                  return (
                    <CategoryRow
                      key={categoryId}
                      categoryId={categoryId}
                      categoryName={category?.name}
                      pricing={pricing}
                      onChangePricing={handleCategoryPricingChange}
                      onDeleteCategory={handleDeleteCategory}
                    />
                  );
                })}
                <CategoryRow
                  categoryId='others'
                  categoryName='Others'
                  pricing={contractInfo.trade_wise_pricing?.['others'] || {}}
                  isDeletable={false}
                  onChangePricing={handleCategoryPricingChange}
                  onDeleteCategory={handleDeleteCategory}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnterpriseConversionForm;
